import { INTEGRATION_PARTNER, MEDICAL_PROVIDER_INPUT_MODE } from './enum.js';

export const UNASSIGNED_PROVIDER_NAME = '__UNASSIGNED__';

export const MEDICAL_PROVIDER_TYPE = Object.freeze({
  POST_ACCIDENT: 'postAccident',
  PRE_ACCIDENT: 'preAccident'
});

export const MEDICAL_RECORD_TYPE = Object.freeze({
  ALL_OTHER_MEDICAL_RECORDS: 'All Other Medical Records',
  MRI_OTHER_IMAGING: 'MRI Other Imaging',
  CONSULTATION_REPORTS: 'Consultation Reports',
  HOSPITAL: 'Hospital',
  SURGERY_CENTER_REPORTS: 'Surgery Center Reports',
  EMERGENCY_ROOM: 'ER'
});

// A partner import stamps its own id on every medical file it brings, so the bucket ends up holding
// one entry called `casepeer` instead of the clinics that treated the plaintiff.
const PARTNER_PLACEHOLDER_NAMES = new Set(
  Object.values(INTEGRATION_PARTNER).map((partnerId) => partnerId.toLowerCase())
);

const getTrimmedProviderName = (providerName) => (providerName ?? '').trim();

/**
 * Reports whether a provider entry is still waiting on AI resolution: an empty name, which is the
 * failed-dedup fallback, or the placeholder the medical assignment stage seeds.
 * @param {string} [providerName] - a medicalRecords[] entry's providerName
 * @returns {boolean}
 */
export const getIsUnresolvedProviderName = (providerName) => {
  const trimmed = getTrimmedProviderName(providerName);
  return trimmed === '' || trimmed === UNASSIGNED_PROVIDER_NAME;
};

/**
 * Reports whether a provider name is one nobody chose. Wider than getIsUnresolvedProviderName by the
 * partner ids, which are names in form only - a file imported from CASEpeer is filed under
 * `casepeer`, and reading that as a provider a human picked would keep the clinic names out of the
 * letter for good.
 * @param {string} [providerName] - a medicalRecords[] entry's providerName
 * @returns {boolean}
 */
export const getIsPlaceholderProviderName = (providerName) =>
  getIsUnresolvedProviderName(providerName) ||
  PARTNER_PLACEHOLDER_NAMES.has(getTrimmedProviderName(providerName).toLowerCase());

const getAllProviderEntries = ({ client }) => [
  ...(client?.medicalProviders?.[MEDICAL_PROVIDER_TYPE.POST_ACCIDENT]?.medicalRecords ?? []),
  ...(client?.medicalProviders?.[MEDICAL_PROVIDER_TYPE.PRE_ACCIDENT]?.medicalRecords ?? [])
];

/**
 * Derives the medical providers input mode for a client by inspecting its
 * existing provider entries. Returns MANUAL when any post- or pre-accident
 * medical record carries a real (hand-typed) provider name, otherwise AI.
 * @param {Object} params
 * @param {Object} params.client - a clients[] entry from a Case document
 * @returns {'AI'|'MANUAL'}
 */
export const deriveMedicalProvidersInputMode = ({ client }) => {
  const hasRealProvider = getAllProviderEntries({ client })
    .some(({ providerName } = {}) => !getIsUnresolvedProviderName(providerName));

  return hasRealProvider ? MEDICAL_PROVIDER_INPUT_MODE.MANUAL : MEDICAL_PROVIDER_INPUT_MODE.AI;
};

/**
 * Reports whether a timing bucket still holds records that need AI provider
 * resolution. A record is unresolved when its providerName is empty/whitespace
 * (the failed-dedup fallback) or the __UNASSIGNED__ placeholder.
 * @param {Object} params
 * @param {Array<{ providerName?: string }>} params.medicalRecords - persisted provider entries
 * @returns {boolean}
 */
export const getHasUnresolvedMedicalRecords = (medicalRecords) => {
  if (medicalRecords?.length) {
    return medicalRecords.some(({ providerName } = {}) => getIsUnresolvedProviderName(providerName));
  }
  return false;
}

